/** Button: primary, secondary, tertiary, destructive; default/hover/focus/active/disabled/loading.
 *
 * Heights come from the control scale in styles.css, so a button and the `.field` input,
 * select or textarea beside it share one baseline: `md` is --control-h (48 px at 100%, the
 * touch-target floor for students and live screens), `sm` is --control-h-sm (40 px), the one
 * documented compact step, for dense teacher tables only -- never on a student or live
 * surface. */
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "destructive";
export type ButtonSize = "md" | "sm";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  /** Teacher-only compact step; the default is the touch-safe height. */
  size?: ButtonSize;
  loading?: boolean;
  /** Full width: the primary action position on every live screen (bottom, full width). */
  block?: boolean;
  children: ReactNode;
};

/* The vertical padding stays under the floor on purpose: with a border (the secondary and
   destructive variants) a taller padding would push the button past --control-h and off the
   baseline the fields sit on. min-height, not padding, decides the height. */
const sizes: Record<ButtonSize, string> = {
  md: "min-h-[var(--control-h)] min-w-[var(--control-h)] px-5 py-2 text-base",
  sm: "min-h-[var(--control-h-sm)] min-w-[var(--control-h-sm)] px-3.5 py-1 text-sm",
};

const styles: Record<ButtonVariant, string> = {
  primary: "bg-[color:var(--accent)] text-[color:var(--accent-ink)] hover:brightness-95 active:brightness-90",
  secondary:
    "bg-transparent text-[color:var(--accent)] border border-[color:var(--accent)] hover:bg-[color:var(--accent)]/10",
  tertiary: "bg-transparent text-[color:var(--accent)] hover:underline",
  destructive: "bg-[color:var(--not-yet)] text-[color:var(--accent-ink)] hover:brightness-95",
};

export function Button({ variant = "primary", size = "md", loading = false, block = false, disabled, className = "", children, ...rest }: Props) {
  const off = disabled || loading;
  return (
    <button
      type="button"
      {...rest}
      onClick={off ? (e) => e.preventDefault() : rest.onClick}
      disabled={disabled}
      aria-disabled={off || undefined}
      aria-busy={loading || undefined}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] font-semibold transition-[filter,background] duration-150",
        sizes[size],
        styles[variant],
        off ? "cursor-not-allowed opacity-60" : "",
        block ? "w-full" : "",
        className,
      ].join(" ")}
    >
      {loading && <span aria-hidden className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
      {children}
    </button>
  );
}
